import React, { useState } from "react";
import { getOrganizerCurrency } from "../../../utils/currency";
import "./EventCardV3.css";

interface EventCardV3Props {
  event: any;
  onPress: () => void;
  onEdit?: (e: React.MouseEvent) => void;
  onPublish?: (e: React.MouseEvent) => void;
  onUnpublish?: (e: React.MouseEvent) => void;
  hideMetrics?: boolean;
  isDraft?: boolean;
  revenue?: number;
  ticketsSold?: number;
  capacity?: number;
}

const formatRevenueCurrency = (val: number) => {
  const currencyInfo = getOrganizerCurrency();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyInfo.code, maximumFractionDigits: 0 }).format(val);
  } catch {
    return `${currencyInfo.symbol}${val.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
};

const EventCardV3: React.FC<EventCardV3Props> = ({
  event,
  onPress,
  onEdit,
  onPublish,
  onUnpublish,
  hideMetrics,
  isDraft,
  revenue = 0,
  ticketsSold = 0,
  capacity = 0,
}) => {
  const [hovered, setHovered] = useState(false);

  let isRole2 = false;
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsedData = JSON.parse(authUser);
      isRole2 = String(parsedData.user?.userRole) === "2";
    }
  } catch (err) {
    console.error("Error parsing authUser", err);
  }

  const poster = event.bannerImgDesktop || event.eventImage || event.thumbImg || event.poster;
  const isImage = poster && (poster.startsWith("http") || poster.startsWith("data:") || poster.startsWith("/"));
  const imageSrc = isImage ? poster : "";
  const venue = event.eventVenue || event.venue || event.eventCity || "No Venue Listed";
  const name = event.eventName || event.name || "Untitled Event";

  return (
    <>


      <div
        className={`ev3-card d-flex flex-column justify-content-between w-100 overflow-hidden${
          hovered ? " is-hovered" : ""
        }`}
        onClick={onPress}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Image Box - 3:4 aspect ratio */}
        <div className="ev3-image-box position-relative w-100 overflow-hidden flex-shrink-0">
          <img src={imageSrc} alt={name} className="w-100 h-100" />
          <div className="ev3-gradient-overlay position-absolute top-0 start-0 w-100 h-100 pe-none" />

          <span
            className={`ev3-badge position-absolute top-0 start-0 mt-2 ms-2 rounded-pill text-uppercase ${
              isDraft ? "ev3-badge-draft" : "ev3-badge-published"
            }`}
          >
            {isDraft ? "Draft" : "Published"}
          </span>
        </div>

        {/* Content Area */}
        <div className="d-flex flex-column justify-content-between flex-grow-1 ev3-min-w-0 text-start pt-2">
          <div className="d-flex flex-column gap-1 ev3-min-w-0">
            <h4 className="ev3-title text-truncate mb-0">{name}</h4>
            <p className="ev3-venue text-truncate text-uppercase mb-0">{venue}</p>
          </div>

          {isRole2 ? (
            <div className="ev3-divider d-flex flex-column gap-2 pt-2 mt-2">
              <div className="d-flex align-items-center justify-content-between gap-1">
                <div className="text-start ev3-min-w-0">
                  <span className="ev3-metric-label text-uppercase d-block mb-1">Sales</span>
                  <span className="ev3-metric-value">
                    {ticketsSold}
                  </span>
                </div>
                <div className="text-end ev3-min-w-0">
                  <span className="ev3-metric-label text-uppercase d-block mb-1">Revenue</span>
                  <span className="ev3-metric-value ev3-metric-revenue">{formatRevenueCurrency(revenue)}</span>
                </div>
              </div>
            </div>
          ) : isDraft ? (
            <div className="ev3-divider d-flex flex-column gap-2 pt-2 mt-2">
              <span className="ev3-draft-note text-truncate d-block">
                Draft Workspace · Ready for launch
              </span>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  className="ev3-btn ev3-btn-publish text-uppercase text-center flex-grow-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPress();
                  }}
                >
                  Go to Draft
                </button>
              </div>
            </div>
          ) : (
            <div className="ev3-divider d-flex flex-column gap-2 pt-2 mt-2">
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  className="ev3-btn ev3-btn-ghost text-uppercase"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.(e);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="ev3-btn ev3-btn-publish text-uppercase text-center flex-grow-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPress();
                  }}
                >
                  Manage
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EventCardV3;